import { useEffect, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { localDigits } from './updatesFormat';

interface UpdatesHeaderProps {
  title: string;
  updatedAt: number;
  isRefreshing: boolean;
  onRefresh: () => void;
}

/** Whole minutes / hours since the answer arrived; under a minute is "now". */
export const updatedAgo = (updatedAt: number, now: number) => {
  const minutes = Math.floor(Math.max(0, now - updatedAt) / 60_000);
  if (minutes < 1) return { unit: 'now' as const, count: 0 };
  if (minutes < 60) return { unit: 'min' as const, count: minutes };
  return { unit: 'h' as const, count: Math.floor(minutes / 60) };
};

/** Re-render every 30 s so "Updated 2 min ago" keeps ticking while the tab is open. */
const useNow = () => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

export function UpdatesHeader({ title, updatedAt, isRefreshing, onRefresh }: UpdatesHeaderProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const now = useNow();
  const ago = updatedAgo(updatedAt, Math.max(now, updatedAt));
  const updated =
    ago.unit === 'now'
      ? String(t('updates_updated_just_now'))
      : ago.unit === 'min'
        ? String(t('updates_updated_minutes', { count: ago.count }))
        : String(t('updates_updated_hours', { count: ago.count }));
  const refreshLabel = String(t('updates_refresh'));

  return (
    <div className='flex items-center justify-between gap-3 px-4 pb-3 pt-4 sm:px-5'>
      <h2 className='m-0 text-xs font-bold uppercase tracking-wide text-sd-foreground'>{title}</h2>
      <div className='flex items-center gap-1'>
        {updatedAt ? (
          <span className='text-[12px] text-sd-muted-foreground'>{localDigits(updated, lang)}</span>
        ) : null}
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label={refreshLabel}
          title={refreshLabel}
        >
          <RotateCw className={isRefreshing ? 'animate-spin' : ''} aria-hidden />
        </Button>
      </div>
    </div>
  );
}
